// The default log level is ERROR in production and WARN otherwise.
// Warnings are what should be seen be external developers integrating the SDK,
// so they should see all these messages in the integration environments.
// The default level can be overwritten by the localStorage key "blink-|-Logger.loggerLevel".

import {LOG_LEVEL, MERCHANT_STORAGE_KEYS} from "../blink-sdk/Constants";
import {merchantLocalStorage} from "../blink-sdk/modules/merchant-state/MerchantStorage";

// Name to level, looked up by name from the storage key and from setLevel()
const Level: Record<string, number> = {
    DEBUG: 0,
    INFO: 1,
    WARN: 2,
    ERROR: 3,
};

// level arrives as a name or a number and is answered with a number, so there is no one type for it
function toNumericLevel(level) {
    if (!level || (typeof level === "number") || (level instanceof Number)) {
        return level;
    }
    const normalizedLevel = level.toString().toUpperCase();
    return Level[normalizedLevel];
}

const consoleHandlers = [
    console.debug,
    console.info,
    console.warn,
    console.error,
];

function GetDefaultLevel() {
    return merchantLocalStorage.getItem(MERCHANT_STORAGE_KEYS.loggerLevel) ?? LOG_LEVEL;
}

export class Logger {
    // Set in the constructor, prefixed onto every message
    declare name: string;
    static Level = Level; // so it can be exported
    static defaultLoggerName = "[BlinkSDK]";

    static currentLevel = toNumericLevel(GetDefaultLevel() || Level.DEBUG);
    static droppedMessages = 0;

    static setLevel(level: string | number) {
        const normalizedLevel = toNumericLevel(level);
        if (!normalizedLevel) {
            // This is actually a console error directly, since it can't really be debugged
            console.error("Invalid log level: ", level);
            return;
        }
        this.currentLevel = normalizedLevel;
    }

    static logMessage(level: number, loggerName: string, ...args: any[]) {
        if (level < this.currentLevel) {
            this.droppedMessages += 1;
            return;
        }

        if (loggerName === null) {
            loggerName = this.defaultLoggerName;
        }

        const newArgs = loggerName ? [loggerName, ...args] : args;

        consoleHandlers[level](...newArgs);
        // Logs could also be collected, so that the client can randomly choose to send everything it collected.
        // We could also dispatch the message here, if we wanted to.
    }

    static log(...args: any[]) {
        this.logMessage(Level.INFO, this.defaultLoggerName, ...args);
    }

    static debug(...args: any[]) {
        this.logMessage(Level.DEBUG, this.defaultLoggerName, ...args);
    }

    static info(...args: any[]) {
        this.logMessage(Level.INFO, this.defaultLoggerName, ...args);
    }

    static warn(...args: any[]) {
        this.logMessage(Level.WARN, this.defaultLoggerName, ...args);
    }

    static error(...args: any[]) {
        this.logMessage(Level.ERROR, this.defaultLoggerName, ...args);
    }

    isDisabled = false;
    level: number | null = null; // An extra level filter on top of the global one, none by default.

    constructor(name: string, level?: string | number) {
        this.name = name;
        if (level) {
            this.setLevel(level);
        }
    }

    setLevel(level: string | number) {
        this.level = toNumericLevel(level);
    }

    disable() {
        this.isDisabled = true;
    }

    enable() {
        this.isDisabled = false;
    }

    logMessage(level: number, ...args: any[]) {
        if (this.isDisabled || (this.level && level < this.level)) {
            // Dropping the message, level too low
            return;
        }
        this.constructor.logMessage(level, this.name, ...args);
    }

    log(...args: any[]) {
        this.logMessage(Level.INFO, ...args);
    }

    debug(...args: any[]) {
        this.logMessage(Level.DEBUG, ...args);
    }

    info(...args: any[]) {
        this.logMessage(Level.INFO, ...args);
    }

    warn(...args: any[]) {
        this.logMessage(Level.WARN, ...args);
    }

    error(...args: any[]) {
        this.logMessage(Level.ERROR, ...args);
    }
}

